import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React from 'react';

import { unoTheme } from '../../../../../../assets/styles/unoTheme';
import MySelect from '../../../../../utils/MySelect';
import UnoButton from '../../../../../utils/UnoButton';
import {
    emptyInstitution, isFilledInstitution, resourceManagerMessage, STEP1_REQUIRED_MESSAGES,
} from './institutionUtils';
import TrademateInstitutionRow from './TrademateInstitutionRow';

const useStyles = makeStyles({
    //Cada grupo de inputs é um cartão: delimita visualmente custodiantes, liquidantes e gestores
    section: {
        border: 'solid 1px ' + unoTheme.borderInputColor,
        borderRadius: '10px',
        padding: '16px 20px 20px 20px',
        marginBottom: '20px',
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '4px',
    },
    sectionTitle: {
        fontSize: '15px',
        fontWeight: 600,
        //color: unoTheme.defaultTextColor,
    },
    hint: {
        fontSize: '12px',
        color: unoTheme.secondaryColor,
        marginBottom: '8px',
        textAlign: 'left'
    },
    empty: {
        fontSize: '13px',
        color: unoTheme.secondaryColor,
        padding: '12px 0px',
    },
    sectionError: {
        fontSize: '12px',
        color: unoTheme.errorColor,
        marginBottom: '8px',
        textAlign: 'left',
    },
    readOnlyList: {
        fontSize: '13px',
        color: unoTheme.defaultTextColor,
    },
    mainCell: {
        width: '90px',
    },
    actionCell: {
        width: '60px',
    },
});

/**
 * Etapa 1 — Dados.
 *
 * Custodiantes e liquidantes são digitados (nome + CNPJ): a tabela `instituitions` não serve como
 * fonte porque a coluna de CNPJ está vazia em todos os registros. Cada lista tem exatamente um
 * principal, escolhido por radio.
 *
 * Os gestores indicados saem dos usuários ROLE_MANAGER ativos vinculados ao RPPS — a flag
 * `is_resource_manager` não participa deste fluxo.
 */
export default function TrademateStep1Data(props) {

    const {
        canEdit, submitted,
        custodians, setCustodians,
        settlements, setSettlements,
        managerIds, setManagerIds,
        availableManagers,
    } = props;

    const classes = useStyles();

    //null quando a regra do Gestor de Recursos está satisfeita
    const resourceMessage = resourceManagerMessage(availableManagers, managerIds);

    const changeItem = (list, setList, index, field, value) => {
        const next = list.map((item, i) => i === index ? Object.assign({}, item, { [field]: value }) : item);
        setList(next);
    };

    const setMain = (list, setList, index) => {
        setList(list.map((item, i) => Object.assign({}, item, { is_main: i === index })));
    };

    const addItem = (list, setList) => {
        //O primeiro da lista já nasce principal: poupa um clique no caso mais comum, de um só
        setList(list.concat([emptyInstitution(list.length === 0)]));
    };

    const removeItem = (list, setList, index) => {

        const next = list.filter((item, i) => i !== index);

        //A seção sempre mostra ao menos uma linha; a em branco é descartada no save
        if (next.length === 0) {
            setList([emptyInstitution(true)]);
            return;
        }

        //Remover o principal deixaria a lista sem nenhum; promove o primeiro que sobrou
        if (!next.some(i => i.is_main)) {
            next[0] = Object.assign({}, next[0], { is_main: true });
        }

        setList(next);
    };

    /**
     * @param {{hint: string, withAccount: boolean, missingMessage: string}} options
     * `withAccount` liga a coluna Conta B3, que só existe para custodiante
     */
    const renderInstitutionSection = (title, singular, list, setList, options) => (
        <div className={classes.section}>

            <div className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>{title}</Typography>

            </div>

            <Typography className={classes.hint}>
                {options.hint}
            </Typography>

            {/* A linha em branco padrão não conta como preenchida, e os validators dela são
                suprimidos de propósito — sem esta mensagem a seção vazia não teria destaque
                nenhum. isFilledInstitution é o mesmo predicado que o save usa para descartar. */}
            {submitted && list.filter(isFilledInstitution).length === 0 && (
                <Typography className={classes.sectionError}>
                    {options.missingMessage}
                </Typography>
            )}

            {list.length === 0 ? (
                <Typography className={classes.empty}>
                    {'Nenhum ' + singular.toLowerCase() + ' informado.'}
                </Typography>
            ) : (
                list.map((item, index) => (
                    <TrademateInstitutionRow
                        key={singular + '-' + index}
                        item={item}
                        singular={singular}
                        canEdit={canEdit}
                        submitted={submitted}
                        withAccount={options.withAccount}
                        onChangeField={(field, value) => changeItem(list, setList, index, field, value)}
                        onSetMain={() => setMain(list, setList, index)}
                        onRemove={() => removeItem(list, setList, index)}
                    />
                ))
            )}

            <div style={{ marginTop: '16px', display: "flex" }}>
                {canEdit && (
                    <UnoButton
                        size='smallish'
                        type='primaryClean'
                        onClick={() => addItem(list, setList)}>
                        {'+ Adicionar ' + singular}
                    </UnoButton>
                )}
            </div>
        </div>
    );

    return (
        <div>

            {renderInstitutionSection('Custodiantes', 'Custodiante', custodians, setCustodians, {
                hint: 'Informe CNPJ, razão social e conta B3 de cada custodiante. '
                    + 'É possível adicionar mais de um, marque qual é o principal.',
                withAccount: true,
                missingMessage: STEP1_REQUIRED_MESSAGES.custodians,
            })}

            {renderInstitutionSection('Liquidantes', 'Liquidante', settlements, setSettlements, {
                hint: 'Informe o CNPJ e a razão social de cada liquidante, é possível adicionar '
                    + 'mais de um. Marque qual é o principal.',
                withAccount: false,
                missingMessage: STEP1_REQUIRED_MESSAGES.settlements,
            })}

            <div className={classes.section}>

                <div className={classes.sectionHeader}>
                    <Typography className={classes.sectionTitle}>{'Gestores para comunicados e assinatura'}</Typography>
                </div>

                <Typography className={classes.hint}>
                    {'Selecione ao menos um gestor. A lista traz os gestores ativos vinculados a este '
                        + 'RPPS, e o Gestor de Recursos tem que estar entre os indicados.'}
                </Typography>

                {/* O MySelect já tem validator próprio, mas quando não há gestor disponível a
                    tela troca o select por um texto — nesse caminho a mensagem abaixo é o único
                    destaque de que a seção está bloqueando o avanço. */}
                {submitted && managerIds.length === 0 && (
                    <Typography className={classes.sectionError}>
                        {STEP1_REQUIRED_MESSAGES.managers}
                    </Typography>
                )}

                {submitted && managerIds.length > 0 && resourceMessage && (
                    <Typography className={classes.sectionError}>
                        {resourceMessage}
                    </Typography>
                )}

                {availableManagers.length === 0 ? (
                    <Typography className={classes.empty}>
                        {'Este RPPS não possui gestores ativos cadastrados. '
                            + 'Cadastre um gestor antes de enviar o Cadastro Trademate.'}
                    </Typography>
                ) : !canEdit ? (
                    //MySelect no modo multiple não repassa `disabled` para o Select do MUI,
                    //então em modo leitura mostramos a seleção como texto
                    <Typography className={classes.readOnlyList}>
                        {managerIds.length === 0
                            ? 'Nenhum gestor indicado.'
                            : availableManagers
                                .filter(m => managerIds.indexOf(m.id) !== -1)
                                .map(m => m.name)
                                .join(', ')}
                    </Typography>
                ) : (
                    <MySelect
                        multiple
                        initblank
                        customWidth={'420px'}
                        valuesMultiple={managerIds}
                        optionsMultiple={availableManagers.map(m => ({
                            value: m.id,
                            //Sem marcar quem é, a regra vira adivinhação: o usuário toma o erro
                            //bloqueante sem saber quem precisa incluir. Mesma convenção do
                            //getPrettyRole, que anexa " DE RECURSOS" ao papel.
                            label: m.is_resource_manager
                                ? m.name + ' (Gestor de Recursos)'
                                : m.name,
                        }))}
                        validator={submitted && managerIds.length === 0 ? true : undefined}
                        helperText={'Selecione ao menos um gestor'}
                        onChange={(values) => setManagerIds(values)}
                    />
                )}

            </div>

        </div>
    );
}
